import { BaseLayerElementCreator } from '@3d-scene/layer/creator/base-layer-element-creator';
import { LabelElement } from '@3d-scene/layer/elements/label-element';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ProjectModel } from '@project/model/project.model';
import { Log } from '@shared/debug/log';
import { ArrayUtils } from '@shared/util/array-utils';
import { SimpleTextValueVO } from '@shared/vo/meta-element.vo';
import { Group, Object3D, Vector3 } from 'three';
import { ContainerService } from '../../model3d/container.service';

export type BmkLabelInfo = {text: string, startContainer: ArticleContainer, endContainer: ArticleContainer, alignment: 'horizontal' | 'vertical'};

export class BmkLayerElementCreator extends BaseLayerElementCreator {
  protected static readonly OFFSET: number = 80;
  protected static readonly INITIAL_LABEL_ROTATION: Vector3 = new Vector3(-90, 0, -90);

  private _terminalBlockLabelingStartZ: number = Number.MIN_VALUE;
  private _longestTerminalBlockLabel: number = Number.MIN_VALUE;

  constructor(
    projectModel: ProjectModel,
    private mountingRailHelper: MountingRailHelper,
    private containerService: ContainerService,
    private articleConnectionService: ArticleConnectionService,
  ) {
    super(projectModel, 'bmk');
  }

  public createLayerElements(): Group {


    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (!Log.assertExists(insertHelper)) {
      return this.createLayerGroup();
    }
    let connectedArticles: ArticleContainer[] = insertHelper.getAllConnectedArticles();
    if (ArrayUtils.isEmpty(connectedArticles)) {
      return this.createLayerGroup();
    }

    const allArticles = this.containerService.enumerateArticleContainersInGlobal();

    const allRailArticles = allArticles.filter((container) => ArticleUtils.isMountingRail(container.article));
    const allArticlesOnRail = allArticles.filter((container) => this.articleConnectionService.isArticleConnectedToMountingRail(container));

    connectedArticles.push(...allRailArticles);

    this._terminalBlockLabelingStartZ = this.findMinZValue(allArticles);

    connectedArticles = connectedArticles.filter((container) => this.isIncludedInCalculation(container));

    const labelInfos = [
      ...this.collectLabelsForArticlesOnRail(allArticlesOnRail),
      ...this.collectLabelsForRails(allRailArticles)];

    const elements = labelInfos.map((labelInfo) => this.createLabelForBmkGroup(labelInfo, connectedArticles));
    return this.createLayerGroup(elements);
  }

  private collectLabelsForArticlesOnRail(containers: ArticleContainer[]): BmkLabelInfo[] {
    containers.sort((a, b) => b.position.x - a.position.x);

    const labels: BmkLabelInfo[] = [];

    let i = 0;
    while (i < containers.length) {
      const container = containers[i];
      let lastContainerOfBmk = container;
      const bmkText: string = this.getArticleBmk(container);

      i++;

      if(!bmkText){
        continue;
      }

      // skip to last container with the same bmk
      while (i < containers.length && bmkText === this.getArticleBmk(containers[i])) {
        lastContainerOfBmk = containers[i];
        i++;
      }

      this.updateLabelsLength(bmkText);
      labels.push({text: bmkText, startContainer: container, endContainer: lastContainerOfBmk, alignment: 'horizontal'});
    }

    return labels;
  }

  private collectLabelsForRails(containers: ArticleContainer[]): BmkLabelInfo[] {
    const labels: BmkLabelInfo[] = [];

    for (const container of containers) {
      let lastContainerOfBmk = container;
      const bmkText: string = this.getArticleBmk(container);
      if(!bmkText){
        continue;
      }

      labels.push({text: bmkText, startContainer: container, endContainer: lastContainerOfBmk, alignment: 'vertical'});
    }

    return labels;
  }

  private getArticleBmk(container: ArticleContainer): string {
    return ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(container.articleUsage, ArticleUsageInfoPropertyDef.CAE_ITEM_STRUCTURE);
  }

  private createLabelForBmkGroup(labelInfo: BmkLabelInfo, articlesOnRail: ArticleContainer[]): Object3D {

    const labelWidth = labelInfo.alignment == 'vertical' ? DimensionUtils.getTextLength(labelInfo.text) : this._longestTerminalBlockLabel;

    const labelElement = new LabelElement(
      this.createTextValue(labelInfo.text),
      LabelElement.DEFAULT_FONT_SIZE,
      BmkLayerElementCreator.INITIAL_LABEL_ROTATION,
      labelWidth
    );
    const labelPosition: Vector3 = labelInfo.alignment == 'vertical' ?
      this.getVerticalLabelPosition(labelInfo.startContainer, labelWidth)
      : this.getHorizontalLabelPosition(labelInfo.startContainer, labelInfo.endContainer, articlesOnRail);

    labelElement.position.set(labelPosition.x, labelPosition.y, labelPosition.z);

    const group = new Group();
    group.add(labelElement);
    const lineElements = this.createBracket(labelPosition, labelInfo)
    group.add(lineElements);
    return group;
  }

  private createBracket(labelPosition: Vector3, labelInfo: BmkLabelInfo): Group {
    const min = this.calculateMinGlobalArticlePoint(labelInfo.startContainer);
    const max = this.calculateMaxGlobalArticlePoint(labelInfo.endContainer);

    if(labelInfo.alignment == 'vertical'){
      const end = new Vector3(min.x + 30, labelPosition.y, min.z);
      const start = new Vector3(min.x + 30, labelPosition.y, max.z);
      return DimensionUtils.createBracket(end, start, Math.PI);
    } else {
      const end = new Vector3(min.x, labelPosition.y, labelPosition.z + 30);
      const start = new Vector3(max.x, labelPosition.y, labelPosition.z + 30);
      return DimensionUtils.createBracket(start, end, Math.PI);
    }

  }

  private getHorizontalLabelPosition(startContainer: ArticleContainer, endContainer: ArticleContainer, articlesOnRail: ArticleContainer[]): Vector3 {
    let startArticleXPosition: number = this.calculateMinGlobalArticlePoint(startContainer).x;
    const endArticleXPosition: number = this.calculateMaxGlobalArticlePoint(endContainer).x;
    const articleIndex: number = articlesOnRail.indexOf(startContainer);
    if (articleIndex > 0) {
      const previousArticle: ArticleContainer = articlesOnRail[articleIndex - 1];
      const maxGlobalPreviousArticlePoint: Vector3 = this.calculateMaxGlobalArticlePoint(previousArticle);
      if (maxGlobalPreviousArticlePoint.x < startArticleXPosition && maxGlobalPreviousArticlePoint.x > endArticleXPosition) {
        startArticleXPosition = maxGlobalPreviousArticlePoint.x;
      }
    }

    let articleZPosition: number = this._terminalBlockLabelingStartZ;

    return new Vector3((startArticleXPosition + endArticleXPosition) / 2, startContainer.position.y, articleZPosition);
  }

  private getVerticalLabelPosition(startContainer: ArticleContainer, labelWidth: number): Vector3 {
    const articleMinPosition = this.calculateMinGlobalArticlePoint(startContainer);
    const articleMaxPosition = this.calculateMaxGlobalArticlePoint(startContainer);
    const center = new Vector3().addVectors(articleMinPosition, articleMaxPosition).multiplyScalar(0.5);

    // why not devide the labelWidth / 2 ? No idea but 3.5 works good enough... maybe because of padding
    const zPosition = center.z + labelWidth / 3.5;

    return new Vector3(articleMinPosition.x + BmkLayerElementCreator.OFFSET, startContainer.position.y, zPosition);
  }

  protected findMinZValue(containers: ArticleContainer[]): number {
    return this.calculateMinGlobalPointOfArticles(containers).z - BmkLayerElementCreator.OFFSET;
  }

  private updateLabelsLength(text: string): void {
    const labelLength: number = DimensionUtils.getTextLength(text);
    this._longestTerminalBlockLabel = Math.max(this._longestTerminalBlockLabel, labelLength);
  }

  public createTextValue(value: string): SimpleTextValueVO {
    const simpleTextValue: SimpleTextValueVO = new SimpleTextValueVO();
    simpleTextValue.text = value;
    simpleTextValue.hAlignment = -1;
    simpleTextValue.vAlignment = 0;
    return simpleTextValue;
  }
}
